import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    apiListProjects,
    apiListShifts,
    apiAssignableUsers,
    apiCreateHandover,
    formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import { toISO, fmtTime } from "@/lib/date";
import { AlertCircle, ClipboardList } from "lucide-react";

const UNASSIGNED = "__unassigned__";

export default function CreateHandoverDialog({ open, onOpenChange, onCreated }) {
    const [projects, setProjects] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [projectId, setProjectId] = useState("");
    const [shiftId, setShiftId] = useState("");
    const [dateStr, setDateStr] = useState(toISO(new Date()));
    const [incomingUserId, setIncomingUserId] = useState(UNASSIGNED);
    const [summary, setSummary] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setError("");
        setSummary("");
        setIncomingUserId(UNASSIGNED);
        setDateStr(toISO(new Date()));
        (async () => {
            try {
                const list = await apiListProjects();
                const active = list.filter((p) => p.active);
                setProjects(active);
                if (active.length && !projectId) {
                    setProjectId(active[0].project_id);
                }
            } catch (err) {
                setError(formatApiError(err));
            }
        })();
        // Only re-run when the dialog opens; projectId is deliberately excluded
        // to preserve any pre-selected project while syncing defaults.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!projectId) return;
        (async () => {
            try {
                const [sh, users] = await Promise.all([
                    apiListShifts(projectId),
                    apiAssignableUsers(projectId).catch(() => []),
                ]);
                setShifts(sh);
                setCandidates(users);
                if (sh.length && !sh.find((s) => s.shift_id === shiftId)) {
                    setShiftId(sh[0].shift_id);
                }
            } catch (err) {
                setError(formatApiError(err));
            }
        })();
        // shiftId is a downstream value the effect keeps in sync — omitting is intentional
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    async function submit(e) {
        e.preventDefault();
        setError("");
        if (!projectId || !shiftId || !dateStr) {
            setError("Project, shift and date are required.");
            return;
        }
        setSaving(true);
        try {
            const ho = await apiCreateHandover({
                project_id: projectId,
                shift_id: shiftId,
                handover_date: dateStr,
                incoming_user_id: incomingUserId === UNASSIGNED ? null : incomingUserId,
                shift_summary: summary || "",
            });
            toast.success("Handover created");
            onOpenChange(false);
            onCreated?.(ho);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px]" data-testid="create-handover-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ClipboardList className="h-4 w-4 text-[#0072BC]" />
                        Start a new handover
                    </DialogTitle>
                    <DialogDescription>
                        You become the outgoing user. Pick the incoming user
                        now or leave it open for the incoming shift to claim.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4" data-testid="create-handover-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Project
                            </Label>
                            <Select value={projectId} onValueChange={setProjectId}>
                                <SelectTrigger className="h-10" data-testid="ho-project-trigger">
                                    <SelectValue placeholder="Choose a project" />
                                </SelectTrigger>
                                <SelectContent>
                                    {projects.map((p) => (
                                        <SelectItem
                                            key={p.project_id}
                                            value={p.project_id}
                                            data-testid={`ho-project-option-${p.project_id}`}
                                        >
                                            {p.name} · {p.code}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Shift
                            </Label>
                            <Select value={shiftId} onValueChange={setShiftId}>
                                <SelectTrigger className="h-10" data-testid="ho-shift-trigger">
                                    <SelectValue placeholder="Choose a shift" />
                                </SelectTrigger>
                                <SelectContent>
                                    {shifts.map((s) => (
                                        <SelectItem
                                            key={s.shift_id}
                                            value={s.shift_id}
                                            data-testid={`ho-shift-option-${s.shift_id}`}
                                        >
                                            {s.name} · {fmtTime(s.start_time)}–{fmtTime(s.end_time)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="ho-date" className="text-[12px] text-ntt-muted mb-1.5 block">
                                Date
                            </Label>
                            <Input
                                id="ho-date"
                                type="date"
                                required
                                value={dateStr}
                                onChange={(e) => setDateStr(e.target.value)}
                                data-testid="ho-date-input"
                                className="h-10 font-mono"
                            />
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Incoming user (optional)
                            </Label>
                            <Select value={incomingUserId} onValueChange={setIncomingUserId}>
                                <SelectTrigger className="h-10" data-testid="ho-incoming-trigger">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={UNASSIGNED} data-testid="ho-incoming-unassigned">
                                        Leave open for incoming shift
                                    </SelectItem>
                                    {candidates.map((c) => (
                                        <SelectItem
                                            key={c.user_id}
                                            value={c.user_id}
                                            data-testid={`ho-incoming-option-${c.user_id}`}
                                        >
                                            {c.full_name} · {c.role}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                            Shift summary (optional now, required to submit)
                        </Label>
                        <textarea
                            value={summary}
                            onChange={(e) => setSummary(e.target.value)}
                            data-testid="ho-summary-input"
                            placeholder="Overall system health and general notes for the incoming shift…"
                            className="w-full min-h-[88px] rounded-md border border-ntt-border p-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0072BC]/40"
                        />
                    </div>

                    {error && (
                        <div
                            data-testid="ho-create-error"
                            className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                        >
                            <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="ho-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving || !projectId || !shiftId}
                            data-testid="ho-create-btn"
                            className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                        >
                            {saving ? "Creating…" : "Create handover"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
